import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, CreditCard, Check, Crown, Zap, Building2,
  Clock, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { token, restaurant } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const currencySymbol = restaurant?.currency || 'EUR';

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, currentRes, historyRes] = await Promise.all([
        fetch(`${API}/subscriptions/plans`, { headers }),
        fetch(`${API}/subscriptions/current`, { headers }),
        fetch(`${API}/subscriptions/history`, { headers }),
      ]);
      if (plansRes.ok) setPlans(await plansRes.json());
      if (currentRes.ok) setCurrent(await currentRes.json());
      if (historyRes.ok) setHistory(await historyRes.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      setCheckingPayment(true);
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await fetch(`${API}/subscriptions/checkout/status/${sessionId}`, { headers });
          const data = await res.json();
          if (data.payment_status === 'paid') {
            toast.success('Abonnement active avec succes !');
            setCheckingPayment(false);
            fetchData();
            return;
          }
          if (attempts < 8) { attempts++; setTimeout(poll, 2000); }
          else { setCheckingPayment(false); toast.error('Verification du paiement expiree'); }
        } catch { setCheckingPayment(false); }
      };
      poll();
    }
  }, [searchParams]);

  const subscribe = async (planId) => {
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error('Erreur de paiement');
    } catch { toast.error('Erreur de connexion'); }
  };

  const cancel = async () => {
    if (!window.confirm('Etes-vous sur de vouloir annuler votre abonnement ?')) return;
    try {
      const res = await fetch(`${API}/subscriptions/cancel`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Abonnement annule');
        fetchData();
      }
    } catch { toast.error('Erreur'); }
  };

  const planColor = { starter: '#6E5E52', pro: '#D4A853', enterprise: '#0A422D', lifetime: '#D4A853' };
  const planIcon = { starter: Zap, pro: Crown, enterprise: Building2, lifetime: Crown };

  if (loading) return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="subscription-page">
      <header className="border-b border-[#1E1E1E] sticky top-0 z-40 bg-[#0C0C0C]/95 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="text-[#666] hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
          <CreditCard className="w-5 h-5 text-[#D4A853]" />
          <span className="font-bold text-lg">Abonnement & Facturation</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {checkingPayment && (
          <div className="bg-[#D4A853]/10 border border-[#D4A853]/30 rounded-2xl p-5 mb-8 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-[#D4A853] animate-spin" />
            <span className="text-[#D4A853] font-medium">Verification du paiement en cours...</span>
          </div>
        )}

        {/* Current Plan */}
        {current && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6 mb-8" data-testid="current-plan">
            <h2 className="text-sm text-[#888] mb-2">Plan actuel</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-black text-[#D4A853]">{current.plan === 'lifetime' ? 'Lifetime' : (current.plan_info?.name || current.plan)}</span>
                {current.plan === 'lifetime' && (
                  <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2.5 py-1 rounded-full">
                    <Check className="w-3 h-3" /> Acces permanent
                  </span>
                )}
                {current.plan === 'trial' && current.trial_ends_at && (
                  <span className="flex items-center gap-1 text-xs text-[#7C3AED] bg-[#7C3AED]/10 px-2.5 py-1 rounded-full">
                    <Clock className="w-3 h-3" /> Essai jusqu'au {new Date(current.trial_ends_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
              {current.subscription?.status === 'active' && current.plan !== 'lifetime' && (
                <Button variant="ghost" size="sm" className="text-[#666] hover:text-[#EF4444]" onClick={cancel} data-testid="cancel-sub">
                  <XCircle className="w-4 h-4 mr-1" /> Annuler
                </Button>
              )}
            </div>
            {current.plan === 'lifetime' ? (
              <p className="text-sm text-[#888] mt-1">Toutes les fonctionnalites — Tables et serveurs illimites — Gratuit a vie</p>
            ) : current.plan_info?.price > 0 ? (
              <p className="text-sm text-[#888] mt-1">{current.plan_info.price.toLocaleString()} {currencySymbol}/mois</p>
            ) : null}
          </div>
        )}

        {/* Plans Grid - Hidden for lifetime */}
        {current?.plan !== 'lifetime' && (
        <>
        <h2 className="text-xl font-bold mb-5">Changer de plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {plans.map(plan => {
            const Icon = planIcon[plan.id] || Zap;
            const color = planColor[plan.id] || '#888';
            const isCurrent = current?.plan === plan.id;
            return (
              <div key={plan.id} className={`rounded-2xl p-6 border transition-all ${
                isCurrent ? 'border-[#D4A853]/50 bg-[#1A1600]' : 'border-[#1E1E1E] bg-[#111] hover:border-[#333]'
              }`} data-testid={`sub-plan-${plan.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-5 h-5" style={{ color }} />
                  <span className="font-bold" style={{ color }}>{plan.name}</span>
                </div>
                <p className="text-2xl font-black text-white mb-1">{plan.price.toLocaleString()} <span className="text-sm text-[#888] font-normal">{currencySymbol}/mois</span></p>
                <p className="text-xs text-[#888] mb-4">
                  {plan.tables === -1 ? 'Tables illimitees' : `${plan.tables} tables`} | {plan.servers === -1 ? 'Serveurs illimites' : `${plan.servers} serveurs`}
                </p>
                {isCurrent ? (
                  <Button disabled className="w-full h-10 rounded-xl bg-[#333] text-[#888]">
                    <Check className="w-4 h-4 mr-1" /> Plan actuel
                  </Button>
                ) : (
                  <Button onClick={() => subscribe(plan.id)}
                    className="w-full h-10 rounded-xl font-bold text-sm bg-[#D4A853] hover:bg-[#C49A48] text-black"
                    data-testid={`subscribe-${plan.id}`}>
                    Souscrire
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        </>
        )}

        {/* Payment History */}
        {history.length > 0 && (
          <div data-testid="payment-history">
            <h2 className="text-xl font-bold mb-4">Historique des paiements</h2>
            <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1E1E1E]">
                    <th className="text-left text-xs text-[#888] px-5 py-3">Date</th>
                    <th className="text-left text-xs text-[#888] px-5 py-3">Plan</th>
                    <th className="text-left text-xs text-[#888] px-5 py-3">Montant</th>
                    <th className="text-left text-xs text-[#888] px-5 py-3">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((tx, i) => (
                    <tr key={i} className="border-b border-[#1E1E1E]/50">
                      <td className="px-5 py-3 text-sm text-[#CCC]">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</td>
                      <td className="px-5 py-3 text-sm text-white font-medium capitalize">{tx.plan_id}</td>
                      <td className="px-5 py-3 text-sm text-[#CCC]">{tx.amount?.toLocaleString()} {tx.currency || currencySymbol}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          tx.status === 'completed' ? 'bg-[#2D6A4F]/15 text-[#2D6A4F]' : 'bg-[#D4A853]/15 text-[#D4A853]'
                        }`}>{tx.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
